import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'

import { useCurrentUser } from '../../../hooks/useCurrentUser'
import useApi from '../../../hooks/useApi'
import Loading from '../../../components/loading'
import VolunteerRow from './VolunteerRow'
import CheckInOutInfoModal from './CheckInOutInfoModal'
import Icon from '../../../components/icon'
import styles from './volunteerList.module.scss'


const TASK_LEADER_ROLE = 'Task Leader'

export default function VolunteerListModal({ show, onHide, event }) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { volunteerGet, volunteerGetWaitlist, getUserFollowing } = useApi()

  const [volunteers, setVolunteers] = useState([])
  const [waitlist, setWaitlist] = useState([])
  const [followingIds, setFollowingIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [showInfoModal, setShowInfoModal] = useState(false)
  const prevShowRef = useRef(show)
  const hasInitializedTaskRef = useRef(false)
  const isFetchingRef = useRef(false)
  const lastFetchedEventIdRef = useRef(null)
  const hasFetchedFollowingRef = useRef(false)
  const optimisticLocksRef = useRef(new Set())

  const fetchData = useCallback(async () => {
    if (!event?.id || isFetchingRef.current || lastFetchedEventIdRef.current === event.id) {
      if (!event?.id) {
        setLoading(false)
      }
      return
    }

    lastFetchedEventIdRef.current = event.id
    isFetchingRef.current = true
    setLoading(true)
    try {
      let followingPromise = Promise.resolve(null)
      if (currentUser?.id && !hasFetchedFollowingRef.current) {
        hasFetchedFollowingRef.current = true
        followingPromise = getUserFollowing({ id: currentUser.id }).catch(() => null)
      }
      
      const [vols, wait, follow] = await Promise.all([
        volunteerGet({ id: event.id }).catch(() => ({ data: null })),
        volunteerGetWaitlist({ id: event.id }).catch(() => ({ data: null })),
        followingPromise,
      ])

      const sortedVolunteers = (vols?.data || []).sort((a, b) => {
        const nameA = a.user?.name || ''
        const nameB = b.user?.name || ''
        return nameA.localeCompare(nameB)
      })
      const sortedWaitlist = (wait?.data || []).sort((a, b) => {
        const nameA = a.user?.name || ''
        const nameB = b.user?.name || ''
        return nameA.localeCompare(nameB)
      })
      
      setVolunteers(prevVolunteers => {
        const filteredVolunteers = sortedVolunteers.map(v => {
          if (optimisticLocksRef.current.has(v.id)) {
            const existing = prevVolunteers.find(p => p.id === v.id)
            return existing || v
          }
          return v
        })
        return filteredVolunteers
      })
      
      setWaitlist(prevWaitlist => {
        const filteredWaitlist = sortedWaitlist.map(v => {
          if (optimisticLocksRef.current.has(v.id)) {
            const existing = prevWaitlist.find(p => p.id === v.id)
            return existing || v
          }
          return v
        })
        return filteredWaitlist
      })

      if (follow?.data) {
        setFollowingIds(new Set(follow.data.map(u => u.id)))
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error)
      setVolunteers([])
      setWaitlist([])
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }, [event?.id, currentUser?.id, volunteerGet, volunteerGetWaitlist, getUserFollowing])

  useEffect(() => {
    const prevShow = prevShowRef.current
    prevShowRef.current = show

    if (prevShow === true && show === false) {
      setVolunteers([])
      setWaitlist([])
      setFollowingIds(new Set())
      setLoading(false)
      setSelectedTaskId(null)
      hasInitializedTaskRef.current = false
      isFetchingRef.current = false
      lastFetchedEventIdRef.current = null
      hasFetchedFollowingRef.current = false
      optimisticLocksRef.current.clear()
      return
    }

    if (show && event?.id) {
      fetchData()
    }
  }, [show, event?.id, fetchData])


  useEffect(() => {
    if (show && event?.tasks?.length > 0 && !hasInitializedTaskRef.current) {
      const firstTaskWithShifts = event.tasks.find(
        task => task.shifts?.length > 0
      )
      if (firstTaskWithShifts) {
        setSelectedTaskId(firstTaskWithShifts.id)
      }
      hasInitializedTaskRef.current = true
    }
  }, [show])

  const enrichVolunteer = useCallback(
    volunteer => ({
      ...volunteer,
      user: {
        ...volunteer.user,
        current_user_follows:
          volunteer.user?.current_user_follows ?? followingIds.has(volunteer.user?.id),
      },
    }),
    [followingIds],
  )

  const handleUpdate = useCallback((updatedVolunteer) => {
    if (updatedVolunteer?.optimisticLock && updatedVolunteer?.id) {
      optimisticLocksRef.current.add(updatedVolunteer.id)
      console.log(`[VolunteerListModal] Registered optimistic lock for volunteer ${updatedVolunteer.id}`)
      return
    }
    
    // if we have an updated volunteer (server-confirmed data), update state and clear lock
    if (updatedVolunteer) {
      optimisticLocksRef.current.delete(updatedVolunteer.id)
      
      setVolunteers(prevVolunteers => {
        const updated = prevVolunteers.map(v => {
          if (v.id === updatedVolunteer.id || v.user?.id === updatedVolunteer.user?.id) {
            return updatedVolunteer
          }
          return v
        })
        return [...updated].sort((a, b) => {
          const nameA = a.user?.name || ''
          const nameB = b.user?.name || ''
          return nameA.localeCompare(nameB)
        })
      })
      
      setWaitlist(prevWaitlist => {
        const updated = prevWaitlist.map(v => {
          if (v.id === updatedVolunteer.id || v.user?.id === updatedVolunteer.user?.id) {
            return updatedVolunteer
          }
          return v
        })
        return [...updated].sort((a, b) => {
          const nameA = a.user?.name || ''
          const nameB = b.user?.name || ''
          return nameA.localeCompare(nameB)
        })
      })
    } else {
      lastFetchedEventIdRef.current = null
      isFetchingRef.current = false
      fetchData().catch(err => console.error('Error refetching volunteers:', err))
    }
  }, [fetchData])


  const uniqueVolunteers = useMemo(() => {
    const seen = new Set()
    return volunteers.filter(volunteer => {
      const userId = volunteer.user?.id
      if (!userId || seen.has(userId)) {
        return false
      }
      seen.add(userId)
      return true
    })
  }, [volunteers])


  const selectedTask = useMemo(() => {
    if (!selectedTaskId || !event?.tasks) return null
    return event.tasks.find(task => task.id === selectedTaskId)
  }, [selectedTaskId, event?.tasks])


  const shiftsByDay = useMemo(() => {
    if (!selectedTask?.shifts) return []
    
    const grouped = {}
    selectedTask.shifts.forEach(shift => {
      if (!shift.start_time) return
      const dayKey = moment.tz(shift.start_time, moment.tz.guess()).format('YYYY-MM-DD')
      if (!grouped[dayKey]) {
        grouped[dayKey] = []
      }
      grouped[dayKey].push(shift)
    })


    return Object.keys(grouped)
      .sort()
      .map(dayKey => ({
        day: dayKey,
        date: moment.tz(grouped[dayKey][0].start_time, moment.tz.guess()),
        shifts: grouped[dayKey].sort((a, b) => {
          const timeA = moment.tz(a.start_time, moment.tz.guess())
          const timeB = moment.tz(b.start_time, moment.tz.guess())
          return timeA.diff(timeB)
        }),
      }))
  }, [selectedTask])


  const formatShiftTime = useCallback(shift => {
    const startTime = moment.tz(shift.start_time, moment.tz.guess())
    const endTime = moment.tz(shift.end_time, moment.tz.guess())
    return `${startTime.format('h:mm A')} ${t('event_card.to', 'to')} ${endTime.format('h:mm A')}`
  }, [t])


  const formatShiftDate = useCallback(date => {
    return date.format('MMMM D')
  }, [])

  const hasTasks = event?.tasks?.length > 0
  const showTaskView = hasTasks && selectedTaskId !== null

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('VolunteerListScreen.volunteer_label', 'Volunteer List')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Loading show={loading} />
        {!loading && (
          <>
            {hasTasks && (
              <div className={styles.taskSelector}>
                <Form.Select
                  value={selectedTaskId || ''}
                  onChange={e => setSelectedTaskId(e.target.value ? parseInt(e.target.value) : null)}
                  className={styles.taskDropdown}
                >
                  <option value="">{t('VolunteerListScreen.select_task', 'Select a Task')}</option>
                  {event.tasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title || `Task ${task.position + 1}`}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}

            {showTaskView && selectedTask && (
              <>
                {selectedTask.title && (
                  <div className={styles.taskDescription}>
                    <strong>{selectedTask.title}</strong>
                    {selectedTask.description && (
                      <p>{selectedTask.description}</p>
                    )}
                  </div>
                )}
                {shiftsByDay.length > 0 ? (
                  <div className={styles.shiftsByDay}>
                    {shiftsByDay.map(({ day, date, shifts }) => (
                      <div key={day} className={styles.shiftDayGroup}>
                        {shifts.map(shift => {
                          const shiftVolunteers = shift.volunteers || []
                          const taskLeaders = selectedTask?.task_leader_users || []
                          
                          const volunteerUserIds = new Set(shiftVolunteers.map(v => v.user?.id).filter(Boolean))
                          
                          const taskLeadersForShift = taskLeaders
                            .filter(leader => leader?.id && !volunteerUserIds.has(leader.id))
                            .map(leader => ({
                              id: `task-leader-${leader.id}-${shift.id}`,
                              user: leader,
                              role: TASK_LEADER_ROLE,
                              created_at: null,
                              isTaskLeader: true,
                            }))
                          
                          const allPeopleForShift = [...taskLeadersForShift, ...shiftVolunteers]
                          
                          const totalCount = allPeopleForShift.length
                          const volunteersNeeded = shift.volunteers_needed || 0
                          
                          return (
                            <div key={shift.id} className={styles.shiftGroup}>
                              <div className={styles.shiftHeader}>
                                <span className={styles.shiftDate}>
                                  {formatShiftDate(date)} • {formatShiftTime(shift)}
                                </span>
                                <span className={styles.shiftCount}>
                                  {totalCount} {t('event_card.of', 'of')} {volunteersNeeded}
                                </span>
                              </div>
                              {allPeopleForShift.length > 0 ? (
                                <>
                                  <div className={styles.volunteerListHeader}>
                                    <div></div>
                                    <button
                                      type="button"
                                      className={styles.infoIconButton}
                                      onClick={() => setShowInfoModal(true)}
                                      aria-label={t('VolunteerListScreen.check_in_out_info.title', 'How to manage Check In/Out of Volunteers')}
                                    >
                                      <Icon name="info-circle" className="info-circle-icon" />
                                    </button>
                                  </div>
                                  <div className={styles.volunteerList}>
                                    {allPeopleForShift.map(person => (
                                      <VolunteerRow
                                        key={person.id}
                                        volunteer={enrichVolunteer(person)}
                                        event={event}
                                        isTaskLeader={person.isTaskLeader}
                                        onUpdate={handleUpdate}
                                      />
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <p className={styles.emptyMessage}>
                                  {t('VolunteerListScreen.no_volunteers_shift', 'No volunteers for this shift yet')}
                                </p>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.emptyMessage}>
                    {t('VolunteerListScreen.no_shifts', 'No shifts found for this task')}
                  </p>
                )}
              </>
            )}

            {!showTaskView && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <span className={styles.count}>
                    {t('VolunteerListScreen.count_label', '{{count}} of {{total}}', {
                      count: uniqueVolunteers.length,
                      total: event.volunteers_needed || '∞',
                    })}
                  </span>
                </div>
                {uniqueVolunteers.length > 0 ? (
                  <>
                    <div className={styles.volunteerListHeader}>
                      <div></div>
                      <button
                        type="button"
                        className={styles.infoIconButton}
                        onClick={() => setShowInfoModal(true)}
                        aria-label={t('VolunteerListScreen.check_in_out_info.title', 'How to manage Check In/Out of Volunteers')}
                      >
                        <Icon name="info-circle" className="info-circle-icon" />
                      </button>
                    </div>
                    <div className={styles.volunteerList}>
                      {uniqueVolunteers.map(volunteer => (
                        <VolunteerRow
                          key={volunteer.id}
                          volunteer={enrichVolunteer(volunteer)}
                          event={event}
                          onUpdate={handleUpdate}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <p className={styles.emptyMessage}>
                    {t('VolunteerListScreen.volunteer_label', 'No volunteers yet')}
                  </p>
                )}

                {waitlist.length > 0 && (
                  <>
                    <div className={styles.sectionHeader}>
                      <h6 className={styles.waitlistTitle}>
                        {t('VolunteerListScreen.waitlist_label', 'Wait List')}
                      </h6>
                    </div>
                    <div className={styles.volunteerList}>
                      {waitlist.map(volunteer => (
                        <VolunteerRow
                          key={volunteer.id}
                          volunteer={enrichVolunteer(volunteer)}
                          event={event}
                          onUpdate={handleUpdate}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('buttons.close')}
        </Button>
      </Modal.Footer>

      <CheckInOutInfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
      />
    </Modal>
  )
}

